import React, { useState } from 'react';
import { ArrowUpRight, ArrowRight, ExternalLink, Globe, Sparkles, CheckCircle2, LayoutGrid } from 'lucide-react';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Reveal, RevealGroup, RevealItem } from '../components/ui/Reveal';
import { FreelanceShowcase } from '../components/FreelanceShowcase';
import { freelanceWork } from '../data/freelanceData';

const CONTACT = 'mailto:rakshanshetty2003@gmail.com?subject=Freelance%20enquiry';

/**
 * One client card.
 *
 * Implements Vercel's canonical `card-marketing` with Level 3 stacked elevation,
 * subtle border-ring hairline, and dedicated information hierarchy:
 * client title, domain, sector badge, live status dot, summary, and explicit contributions.
 */
function ClientCard({ item }) {
  const isPreview = item.status === 'preview';

  return (
    <Card
      as="a"
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      interactive
      className="group flex flex-col justify-between h-full p-6 sm:p-7 transition-all duration-200 focus-visible:z-10 focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none"
    >
      <div>
        {/* Header: Client name + Sector/Status + External affordance */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="mb-1.5">
              <span className="t-mono text-xs text-[var(--fg-secondary)] truncate block">
                {item.domain}
              </span>
            </div>
            <h3 className="t-h4 text-[var(--fg-primary)] group-hover:text-[var(--accent)] transition-colors">
              {item.client}
            </h3>
          </div>

          <div className="p-2 rounded-md bg-[var(--bg-recessed)] text-[var(--fg-muted)] group-hover:text-[var(--fg-primary)] transition-colors shrink-0 shadow-[var(--border-ring)]">
            <ArrowUpRight aria-hidden="true" size={16} />
          </div>
        </div>

        {/* Sector badge */}
        <div className="mb-4">
          <Badge className="text-[11px] py-0.5">
            {item.sector}
          </Badge>
        </div>

        {/* Summary */}
        <p className="text-sm leading-relaxed text-[var(--fg-secondary)] mb-5">
          {item.summary}
        </p>

        {/* What was engineered / shipped */}
        {item.contributions && item.contributions.length > 0 && (
          <div className="pt-4 border-t border-[var(--bg-recessed)] mb-4">
            <p className="t-eyebrow text-[var(--fg-secondary)] mb-3">Key contributions</p>
            <ul className="space-y-2">
              {item.contributions.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2.5 text-xs leading-relaxed text-[var(--fg-secondary)]"
                >
                  <CheckCircle2
                    size={13}
                    className="shrink-0 mt-0.5 text-[var(--fg-muted)] group-hover:text-[var(--accent)] transition-colors"
                  />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Footer: Live preview indicator & CTA */}
      <div className="pt-4 border-t border-[var(--bg-recessed)] flex items-center justify-between mt-auto">
        <span className="t-mono text-xs text-[var(--fg-muted)]">
          {isPreview ? 'Preview Deployment' : 'Production Site'}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--fg-primary)] group-hover:text-[var(--accent)] transition-colors">
          Visit site
          <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Card>
  );
}

export default function Freelance() {
  const [viewMode, setViewMode] = useState('showcase'); // 'showcase' | 'grid'

  return (
    <Section id="freelance">
      {/* Header & View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="t-h2 text-[var(--fg-primary)] tracking-tight">Freelance & Consulting</h2>
          <p className="text-sm sm:text-base text-[var(--fg-secondary)] max-w-2xl leading-relaxed mt-2">
            Production web applications, client solutions, and digital platforms shipped independently.
          </p>
        </div>

        {/* View Mode Toggle: Showcase vs Grid */}
        <div className="flex items-center self-start sm:self-auto p-1 rounded-lg bg-[var(--bg-recessed)] shadow-[var(--border-ring)] shrink-0">
          <button
            type="button"
            onClick={() => setViewMode('showcase')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'showcase'
                ? 'bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
            }`}
          >
            <Sparkles size={13} />
            <span>Showcase</span>
          </button>

          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'grid'
                ? 'bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
            }`}
          >
            <LayoutGrid size={13} />
            <span>Grid</span>
          </button>
        </div>
      </div>

      {viewMode === 'showcase' ? (
        <div className="space-y-8">
          <FreelanceShowcase items={freelanceWork} />

          {/* Quick Contact Banner */}
          <div className="rounded-xl border border-[var(--bg-recessed)] bg-[var(--bg-elevated)] p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-[var(--shadow-sm)]">
            <div className="space-y-1.5 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start">
                <span className="t-mono text-xs uppercase tracking-wider text-[var(--fg-secondary)] font-medium">Available for select projects</span>
              </div>
              <h4 className="t-h4 text-[var(--fg-primary)]">Ready to engineer something exceptional?</h4>
              <p className="text-sm text-[var(--fg-secondary)] max-w-xl">
                Taking on select web platforms, full-stack architectures, and AI/ML integrations.
              </p>
            </div>

            <Button asChild variant="primary" size="md" className="shrink-0">
              <a href={CONTACT}>
                <span>Start a conversation</span>
                <ArrowRight size={14} className="ml-2" />
              </a>
            </Button>
          </div>
        </div>
      ) : (
        /* Uniform grid of client work */
        <RevealGroup className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
          {freelanceWork.map((item) => (
            <RevealItem key={item.id} className="h-full">
              <ClientCard item={item} />
            </RevealItem>
          ))}

          {/* Featured Polarity-Flipped Availability Card (pricing-card-featured style) */}
          <RevealItem className="h-full">
            <div className="flex flex-col justify-between h-full p-6 sm:p-7 rounded-xl bg-[var(--fg-primary)] text-[var(--bg-canvas)] shadow-[var(--shadow-md)] transition-colors">
              <div>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center">
                    <span className="t-mono text-xs opacity-80 uppercase tracking-wider">
                      Available Now
                    </span>
                  </div>
                  <div className="p-2 rounded-md bg-[var(--bg-canvas)] text-[var(--fg-primary)] opacity-90 shadow-sm">
                    <Sparkles size={16} />
                  </div>
                </div>

                <h3 className="t-h4 font-medium mb-3 text-[var(--bg-canvas)]">
                  Have a project in mind?
                </h3>

                <p className="text-sm leading-relaxed opacity-80 mb-6">
                  Taking on select web development, full-stack architectures, and AI/ML integrations.
                  Tell me what you are building and let us engineer something exceptional.
                </p>

                <div className="space-y-2.5 py-4 border-y border-[var(--bg-canvas)]/15 mb-6 text-xs opacity-90">
                  <div className="flex items-center gap-2">
                    <Globe size={13} className="shrink-0" />
                    <span>Full-Stack Web & Next.js Platforms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles size={13} className="shrink-0" />
                    <span>RAG Pipelines & Custom AI Integrations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ExternalLink size={13} className="shrink-0" />
                    <span>Fast turnarounds & transparent communication</span>
                  </div>
                </div>
              </div>

              <Button
                asChild
                variant="secondary"
                size="lg"
                className="w-full justify-center bg-[var(--bg-canvas)] text-[var(--fg-primary)] hover:bg-[var(--bg-canvas)]/90 shadow-sm font-medium"
              >
                <a href={CONTACT}>
                  Start a conversation
                  <ArrowRight size={15} className="ml-2" />
                </a>
              </Button>
            </div>
          </RevealItem>
        </RevealGroup>
      )}
    </Section>
  );
}
